import { displayText } from '../../../terminology/store';
import { useTerminology } from '../../../terminology/TerminologyContext';
// AdminDashboard.jsx
import React, { useState, useEffect, useRef } from 'react';
import { useNavigate, useLocation, Outlet } from 'react-router-dom';
import axios from 'axios';
import Resellers from '../pages/Resellers.js';
import Clients from '../pages/Clients.js';
import EnterpriseClients from '../pages/EnterpriseClients.js';
import Account from '../pages/Account.js';
import Reports from '../pages/Reports.js';
import ThemeContext from '../../ThemeContext';

class ErrorBoundary extends React.Component {
  state = { hasError: false, error: null };

  static getDerivedStateFromError(error) {
    return { hasError: true, error };
  }

  render() {
    if (this.state.hasError) {
      return (
        <div
          style={{
            padding: '0.75rem 1.25rem',
            marginBottom: '1rem',
            border: `1px solid ${this.context.theme.colors.errorBorder}`,
            borderRadius: '0.25rem',
            color: this.context.theme.colors.errorText,
            backgroundColor: this.context.theme.colors.errorBg,
            textAlign: 'center',
            width: '100%',
            maxWidth: 'min(90vw, 1100px)',
            boxSizing: 'border-box',
          }}
        >
          Something went wrong: {this.state.error?.message || 'Unknown error'}
        </div>
      );
    }
    return this.props.children;
  }
}

ErrorBoundary.contextType = ThemeContext;

const AdminDashboard = () => {
  useTerminology();
  const { theme } = React.useContext(ThemeContext);
  const [activeTab, setActiveTab] = useState('resellers');
  const [activeSubTab, setActiveSubTab] = useState('core');
  const [users, setUsers] = useState([]);
  const [error, setError] = useState('');
  const [currentUser, setCurrentUser] = useState(
    JSON.parse(localStorage.getItem('user')) || {}
  );
  const [isClientsDropdownOpen, setIsClientsDropdownOpen] = useState(false);
  const [isReportsDropdownOpen, setIsReportsDropdownOpen] = useState(false);

  const navigate = useNavigate();
  const location = useLocation();
  const clientsDropdownRef = useRef(null);
  const reportsDropdownRef = useRef(null);

  // detect role & basePath
  const userFromStorage = JSON.parse(localStorage.getItem('user') || '{}') || {};
  const isSuperadmin = String(userFromStorage.role || '').toLowerCase() === 'superadmin';
  const basePath = isSuperadmin ? '/superadmin' : '/admin';

  // Typography from theme
  const fontFamily =
    theme.typography?.fontFamily ||
    '"Roboto", "Helvetica Neue", Arial, sans-serif';
  const bodyFontSize = theme.typography?.bodyFontSize || 14;

  // Sync URL -> activeTab / subTab and fetch resellers when appropriate
  useEffect(() => {
    const pathSegments = location.pathname.split('/').filter(Boolean);
    const lastSegment = pathSegments[pathSegments.length - 1];
    const validTabs = ['resellers', 'clients', 'account', 'reports'];

    // Check for client-related routes, including nested edit routes
    if (pathSegments.includes('client') || lastSegment === 'clients') {
      setActiveTab('clients');
      setActiveSubTab('core');
    } else if (
      pathSegments.includes('enterprise-client') ||
      lastSegment === 'enterprise-clients'
    ) {
      setActiveTab('clients');
      setActiveSubTab('enterprise');
    } else if (location.pathname.includes('/reports')) {
      // look for query param ?sub=user|commission|usage
      const params = new URLSearchParams(location.search);
      const sub = params.get('sub');
      setActiveTab('reports');
      setActiveSubTab(sub === 'commission' || sub === 'usage' ? sub : 'user');
    } else {
      setActiveTab(validTabs.includes(lastSegment) ? lastSegment : 'resellers');
      setActiveSubTab('core');
    }

    // fetch resellers only when activeTab === 'resellers'
    const fetchData = async () => {
      if (activeTab !== 'resellers') return;
      try {
        const token = localStorage.getItem('token');
        const response = await axios.get(
          `${process.env.REACT_APP_BACKEND_URL}/api/webmanager/resellers`,
          {
            headers: { Authorization: `Bearer ${token}` },
          }
        );
        const fetchedUsers = Array.isArray(response.data.data)
          ? response.data.data
          : [];
        setUsers(fetchedUsers);
        if (fetchedUsers.length === 0) {
          setError(displayText('No resellers found in the database.'));
        } else {
          setError('');
        }
      } catch (err) {
        if (err.response?.status === 401) {
          setError('Unauthorized access. Please log in again.');
          localStorage.removeItem('token');
          localStorage.removeItem('user');
          navigate('/login');
        } else {
          setError(displayText('Failed to fetch resellers. Please try again.'));
          setUsers([]);
        }
      }
    };
    fetchData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [navigate, location.pathname, location.search, activeTab]);

  // Close dropdowns on outside click
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (
        clientsDropdownRef.current &&
        !clientsDropdownRef.current.contains(event.target)
      ) {
        setIsClientsDropdownOpen(false);
      }
      if (
        reportsDropdownRef.current &&
        !reportsDropdownRef.current.contains(event.target)
      ) {
        setIsReportsDropdownOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  const handleTabClick = (tab) => {
    // Do NOT navigate on main "clients" or "reports" click so dropdown works
    if (tab === 'clients' || tab === 'reports') {
      // toggle dropdown open state
      if (tab === 'clients') {
        setIsClientsDropdownOpen((s) => !s);
        setIsReportsDropdownOpen(false);
      } else {
        setIsReportsDropdownOpen((s) => !s);
        setIsClientsDropdownOpen(false);
      }
      setActiveTab(tab);
      return;
    }

    setActiveTab(tab);
    setActiveSubTab('core');
    setError('');
    setIsClientsDropdownOpen(false);
    setIsReportsDropdownOpen(false);
    navigate(`${basePath}/${tab}`);
  };

  const handleSubTabClick = (subTab) => {
    setActiveTab('clients');
    setActiveSubTab(subTab);
    setError('');
    const path = subTab === 'enterprise' ? 'enterprise-clients' : 'clients';
    navigate(`${basePath}/${path}`);
    setIsClientsDropdownOpen(false);
  };

  const handleReportSubTab = (subTab) => {
    setActiveTab('reports');
    setActiveSubTab(subTab);
    setError('');
    navigate(`${basePath}/reports?sub=${subTab}`);
    setIsReportsDropdownOpen(false);
  };

  const renderContent = () => {
    // Check if the current path is for a nested route that should render via Outlet
    const pathSegments = location.pathname.split('/').filter(Boolean);
    if (
      pathSegments.includes('enterprise-client') ||
      pathSegments.includes('client') ||
      pathSegments.includes('reseller') ||
      (pathSegments.includes('resellers') &&
        pathSegments.includes('hierarchy'))
    ) {
      return <Outlet />;
    }

    // Render tab-based content for main tabs
    switch (activeTab) {
      case 'resellers':
        return <Resellers users={users} setUsers={setUsers} />;
      case 'clients':
        return activeSubTab === 'core' ? (
          <Clients users={users} />
        ) : (
          <EnterpriseClients />
        );
      case 'account':
        return (
          <Account
            currentUser={currentUser}
            setCurrentUser={setCurrentUser}
            setError={setError}
          />
        );
      case 'reports':
        // pass activeSubTab so Reports can switch between user/commission/usage
        return <Reports activeSubTab={activeSubTab} />;
      default:
        return (
          <div
            style={{
              padding: '1rem',
              textAlign: 'center',
              fontSize: `${bodyFontSize + 2}px`,
              color: '#555',
              fontFamily,
            }}
          >
            This section is under development.
          </div>
        );
    }
  };

  return (
    <ErrorBoundary>
      <div
        style={{
          fontFamily,
          fontSize: `${bodyFontSize}px`,
          width: '100%',
          boxSizing: 'border-box',
          textAlign: 'initial',
        }}
      >
        <div
          style={{
            display: 'flex',
            gap: '0.5rem',
            marginBottom: '1rem',
            justifyContent: 'center',
            flexWrap: 'wrap',
          }}
        >
          {/* role-aware top tabs */}
          {(isSuperadmin
            ? ['resellers', 'clients', 'reports', 'account']
            : ['resellers', 'clients', 'reports', 'account']
          ).map((tab) => (
            <div
              key={tab}
              style={{ position: 'relative', flex: '1 1 0', minWidth: '100px' }}
              ref={
                tab === 'clients'
                  ? clientsDropdownRef
                  : tab === 'reports'
                  ? reportsDropdownRef
                  : null
              }
              onMouseEnter={() => {
                if (tab === 'clients') setIsClientsDropdownOpen(true);
                if (tab === 'reports') setIsReportsDropdownOpen(true);
              }}
              onMouseLeave={() => {
                if (tab === 'clients') setIsClientsDropdownOpen(false);
                if (tab === 'reports') setIsReportsDropdownOpen(false);
              }}
            >
              <button
                style={{
                  width: '100%',
                  padding: '0.4rem',
                  fontSize: `${bodyFontSize}px`,
                  fontWeight: 'bold',
                  textAlign: 'center',
                  borderRadius: '5px',
                  border: 'none',
                  background:
                    activeTab === tab
                      ? theme.gradients.primary
                      : theme.gradients.secondary,
                  color: theme.colors.textWhite,
                  cursor: ['clients', 'reports'].includes(tab) ? 'default' : 'pointer',
                  transition: 'transform 0.2s ease, opacity 0.3s ease',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  gap: '0.5rem',
                  fontFamily,
                }}
                onClick={() => handleTabClick(tab)}
                onMouseOver={(e) =>
                  !['clients', 'reports'].includes(tab) && (e.target.style.opacity = '0.9')
                }
                onMouseOut={(e) =>
                  !['clients', 'reports'].includes(tab) && (e.target.style.opacity = '1')
                }
              >
                <i
                  className={`bi bi-${
                    tab === 'resellers'
                      ? 'briefcase-fill'
                      : tab === 'clients'
                      ? 'people-fill'
                      : tab === 'reports'
                      ? 'file-earmark-fill'
                      : 'person-lines-fill'
                  }`}
                  style={{
                    fontSize: `${Math.max(bodyFontSize - 2, 10)}px`,
                  }}
                ></i>
                {displayText(tab.charAt(0).toUpperCase() +
                  tab.slice(1).replace('account', 'My Account'))}
              </button>

              {/* Clients dropdown */}
              {tab === 'clients' && isClientsDropdownOpen && (
                <div
                  style={{
                    position: 'absolute',
                    top: '100%',
                    left: 0,
                    backgroundColor: theme.colors.bgPrimary,
                    border: `1px solid ${theme.colors.borderColor}`,
                    borderRadius: '5px',
                    boxShadow: '0 2px 5px rgba(0,0,0,0.2)',
                    zIndex: 10,
                    width: '100%',
                    minWidth: '100px',
                    fontFamily,
                  }}
                >
                  <button
                    style={{
                      width: '100%',
                      padding: '0.5rem 1rem',
                      fontSize: `${bodyFontSize}px`,
                      fontWeight: 'normal',
                      textAlign: 'center',
                      background:
                        activeSubTab === 'core'
                          ? theme.gradients.primary
                          : 'transparent',
                      color:
                        activeSubTab === 'core'
                          ? theme.colors.textWhite
                          : theme.colors.textPrimary,
                      border: 'none',
                      cursor: 'pointer',
                      transition: 'background 0.2s ease',
                      fontFamily,
                    }}
                    onClick={() => handleSubTabClick('core')}
                    onMouseOver={(e) =>
                      (e.target.style.background = theme.gradients.secondary)
                    }
                    onMouseOut={(e) =>
                      (e.target.style.background =
                        activeSubTab === 'core'
                          ? theme.gradients.primary
                          : 'transparent')
                    }
                  >{displayText(" Court Client ")}</button>
                  <button
                    style={{
                      width: '100%',
                      padding: '0.5rem 1rem',
                      fontSize: `${bodyFontSize}px`,
                      fontWeight: 'normal',
                      textAlign: 'center',
                      background:
                        activeSubTab === 'enterprise'
                          ? theme.gradients.primary
                          : 'transparent',
                      color:
                        activeSubTab === 'enterprise'
                          ? theme.colors.textWhite
                          : theme.colors.textPrimary,
                      border: 'none',
                      cursor: 'pointer',
                      transition: 'background 0.2s ease',
                      fontFamily,
                    }}
                    onClick={() => handleSubTabClick('enterprise')}
                    onMouseOver={(e) =>
                      (e.target.style.background = theme.gradients.secondary)
                    }
                    onMouseOut={(e) =>
                      (e.target.style.background =
                        activeSubTab === 'enterprise'
                          ? theme.gradients.primary
                          : 'transparent')
                    }
                  >{displayText(" Enterprise Client ")}</button>
                </div>
              )}

              {/* Reports dropdown */}
              {tab === 'reports' && isReportsDropdownOpen && (
                <div
                  style={{
                    position: 'absolute',
                    top: '100%',
                    left: 0,
                    backgroundColor: theme.colors.bgPrimary,
                    border: `1px solid ${theme.colors.borderColor}`,
                    borderRadius: '5px',
                    boxShadow: '0 2px 5px rgba(0,0,0,0.2)',
                    zIndex: 10,
                    width: '100%',
                    minWidth: '120px',
                    fontFamily,
                    overflow: 'hidden',
                  }}
                >
                  {[
                    { key: 'user', label: 'User Report' },
                    { key: 'commission', label: 'Commission Report' },
                    { key: 'usage', label: 'Usage Report' },
                  ].map((item, idx) => (
                    <React.Fragment key={item.key}>
                      <button
                        style={{
                          width: '100%',
                          padding: '0.5rem 1rem',
                          fontSize: `${bodyFontSize}px`,
                          fontWeight: 'normal',
                          textAlign: 'center',
                          background:
                            activeSubTab === item.key ? theme.gradients.primary : 'transparent',
                          color:
                            activeSubTab === item.key
                              ? theme.colors.textWhite
                              : theme.colors.textPrimary,
                          border: 'none',
                          cursor: 'pointer',
                          transition: 'background 0.2s ease',
                          fontFamily,
                        }}
                        onClick={() => handleReportSubTab(item.key)}
                        onMouseOver={(e) =>
                          (e.target.style.background = theme.gradients.secondary)
                        }
                        onMouseOut={(e) =>
                          (e.target.style.background =
                            activeSubTab === item.key
                              ? theme.gradients.primary
                              : 'transparent')
                        }
                      >
                        {item.label}
                      </button>
                      {idx < 2 && (
                        <hr
                          style={{
                            margin: 0,
                            border: 'none',
                            borderTop: `1px solid ${theme.colors.borderColor}`,
                            opacity: 0.5,
                          }}
                        />
                      )}
                    </React.Fragment>
                  ))}
                </div>
              )}
            </div>
          ))}
        </div>

        {error && (
          <div
            style={{
              padding: '0.75rem 1.25rem',
              marginBottom: '1rem',
              border: `1px solid ${theme.colors.errorBorder}`,
              borderRadius: '0.25rem',
              color: theme.colors.errorText,
              backgroundColor: theme.colors.errorBg,
              textAlign: 'center',
              width: '100%',
              boxSizing: 'border-box',
              fontFamily,
              fontSize: `${bodyFontSize}px`,
            }}
          >
            {error}
          </div>
        )}

        <div style={{ width: '100%', boxSizing: 'border-box' }}>
          {renderContent()}
        </div>
      </div>
    </ErrorBoundary>
  );
};

export default AdminDashboard;
